import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Loader2, AlertTriangle, CheckCircle2, Clock, Package,
  User, Calendar, Mail, Hash, MessageSquare, StickyNote,
} from 'lucide-react';
import { toast } from 'sonner';
import { difformityService, DifformityReport } from '../../services/difformityService';

const STATUS_MAP: Record<string, { label: string; color: string; Icon: React.ElementType }> = {
  nuova: { label: 'Nuova', color: 'bg-red-100 text-red-700', Icon: AlertTriangle },
  in_gestione: { label: 'In Gestione', color: 'bg-amber-100 text-amber-700', Icon: Clock },
  chiusa: { label: 'Chiusa', color: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 },
};

function fmtDateTime(iso: string | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso?.slice(0, 16) ?? '—'; }
}

function Field({ label, value, icon: Icon, mono }: {
  label: string; value: React.ReactNode; icon?: React.ElementType; mono?: boolean;
}) {
  const isEmpty = value === undefined || value === null || value === '';
  return (
    <div className="flex flex-col gap-0.5 py-2 border-b border-secondary/60 last:border-b-0">
      <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}{label}
      </span>
      <span className={`text-sm ${mono ? 'font-mono' : ''} ${isEmpty ? 'text-muted-foreground italic' : 'text-foreground'}`}>
        {isEmpty ? '—' : value}
      </span>
    </div>
  );
}

export default function AdminDifformityDetailPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const [report, setReport] = useState<DifformityReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');

  const load = useCallback(async () => {
    if (!reportId) return;
    setLoading(true);
    setNotFound(false);
    try {
      const res = await difformityService.adminGet(reportId);
      setReport(res);
      setNoteDraft(res.adminNote || '');
    } catch (err: any) {
      if (err?.response?.status === 404) setNotFound(true);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (newStatus: string) => {
    if (!report) return;
    setUpdating(true);
    try {
      const res = await difformityService.adminUpdate(report.id, { status: newStatus, adminNote: noteDraft });
      setReport(res);
      toast.success('Stato aggiornato');
    } catch {
      toast.error('Errore aggiornamento stato');
    } finally {
      setUpdating(false);
    }
  };

  const saveNote = async () => {
    if (!report) return;
    setUpdating(true);
    try {
      const res = await difformityService.adminUpdate(report.id, { status: report.status, adminNote: noteDraft });
      setReport(res);
      toast.success('Nota interna salvata');
    } catch {
      toast.error('Errore salvataggio nota');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]" data-testid="admin-difformity-detail-loading">
        <div className="text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-[hsl(214,58%,41%)]" />
          <p className="text-sm text-muted-foreground">Caricamento segnalazione…</p>
        </div>
      </div>
    );
  }

  if (notFound || !report) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]" data-testid="admin-difformity-detail-not-found">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
          <h1 className="text-xl font-semibold mb-2">Segnalazione non trovata</h1>
          <p className="text-sm text-muted-foreground mb-5">
            La segnalazione <span className="font-mono">{reportId}</span> non esiste o non è accessibile.
          </p>
          <Link
            to="/admin/difformita"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium hover:bg-[hsl(206,54%,28%)] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Torna alle segnalazioni
          </Link>
        </div>
      </div>
    );
  }

  const st = STATUS_MAP[report.status] ?? STATUS_MAP.nuova;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto" data-testid="admin-difformity-detail-page">
      <Link
        to="/admin/difformita"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        data-testid="admin-difformity-detail-back"
      >
        <ArrowLeft className="w-4 h-4" /> Torna alle segnalazioni
      </Link>

      {/* Header */}
      <div className="rounded-xl border bg-white shadow-sm p-4 sm:p-5 mb-5">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-[11px] font-bold text-[hsl(214,58%,41%)] tracking-wide font-mono">{report.id}</span>
              <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full ${st.color}`}>
                <st.Icon className="w-3 h-3" />{st.label}
              </span>
            </div>
            <h1 className="text-lg sm:text-xl font-bold tracking-tight" data-testid="admin-difformity-detail-title">
              <AlertTriangle className="w-5 h-5 inline-block mr-2 -mt-0.5 text-[hsl(214,58%,41%)]" />
              Segnalazione {report.id}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-muted-foreground">
              <span><Calendar className="w-3 h-3 inline-block mr-1" />{fmtDateTime(report.createdAt)}</span>
              <span>•</span>
              <span className="font-mono">{report.sku}</span>
            </div>
          </div>

          {/* Status action */}
          <div className="flex flex-col sm:flex-row gap-2 lg:flex-col lg:items-stretch">
            <select
              value={report.status}
              disabled={updating}
              onChange={e => changeStatus(e.target.value)}
              className="h-9 text-sm border rounded-lg px-3 bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]/20"
              data-testid="admin-difformity-detail-status-select"
            >
              {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        {/* Cliente */}
        <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]">
            <User className="w-4 h-4 text-[hsl(214,58%,41%)]" />
            <h2 className="text-sm font-semibold tracking-tight">Cliente</h2>
          </div>
          <div className="p-4">
            <Field label="Ragione sociale" value={report.clientCompany} />
            <Field label="Email" value={report.clientEmail} icon={Mail} />
            <Field label="Codice cliente (ERP)" value={report.an_conto} mono />
          </div>
        </div>

        {/* Prodotto */}
        <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]">
            <Package className="w-4 h-4 text-[hsl(214,58%,41%)]" />
            <h2 className="text-sm font-semibold tracking-tight">Prodotto</h2>
          </div>
          <div className="p-4">
            <Field label="SKU" value={report.sku} icon={Hash} mono />
            <Field label="Titolo" value={report.productTitle} />
            <Field label="Marca" value={report.productBrand} />
            <Link
              to={`/admin/prodotti/${report.sku}`}
              className="inline-block mt-2 text-xs text-[hsl(214,58%,41%)] hover:underline font-medium"
              data-testid="admin-difformity-detail-product-link"
            >
              Vai alla scheda prodotto →
            </Link>
          </div>
        </div>
      </div>

      {/* Messaggio cliente */}
      <div className="rounded-xl border bg-white shadow-sm overflow-hidden mb-5">
        <div className="flex items-center gap-2 px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]">
          <MessageSquare className="w-4 h-4 text-[hsl(214,58%,41%)]" />
          <h2 className="text-sm font-semibold tracking-tight">Messaggio del cliente</h2>
        </div>
        <div className="p-4">
          <p className="text-sm whitespace-pre-wrap" data-testid="admin-difformity-detail-message">{report.message}</p>
        </div>
      </div>

      {/* Nota interna admin */}
      <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b bg-[hsl(var(--dtk-canvas))]">
          <StickyNote className="w-4 h-4 text-[hsl(214,58%,41%)]" />
          <h2 className="text-sm font-semibold tracking-tight">Nota interna</h2>
          <span className="text-[10px] text-muted-foreground ml-1">(visibile solo al team DTK)</span>
        </div>
        <div className="p-4">
          <textarea
            value={noteDraft}
            onChange={e => setNoteDraft(e.target.value)}
            placeholder="Aggiungi una nota interna su questa segnalazione..."
            className="w-full h-24 px-3 py-2 rounded-lg border text-sm resize-none bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]/20"
            disabled={updating}
            data-testid="admin-difformity-detail-note-textarea"
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={saveNote}
              disabled={updating || noteDraft === (report.adminNote || '')}
              className="h-8 px-3 rounded-lg bg-[hsl(214,58%,41%)] text-white text-xs font-medium disabled:opacity-50"
              data-testid="admin-difformity-detail-save-note"
            >
              {updating ? 'Salvataggio…' : 'Salva nota'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
